import clsx from 'clsx'
import React, {
  useCallback,
  useContext, useEffect, useMemo,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import LocationFloatingPanel from '../Panels/LocationFloatingPanel'
import SearchFormField from '../../SearchFormField/SearchFormField'
import {
  CAR_HIRE_SEARCH_TYPES,
} from 'constants/carHire'
import useCarHireSearchLabels from 'hooks/Search/useCarHireSearchLabels'
import useStepsController from 'components/SearchV2/Hooks/useStepsController'
import useDateSearchProps from 'components/SearchV2/Hooks/useDateSearchProps'
import DateFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/DateFloatingPanel'
import SearchFormFieldGroup from '../../SearchFormField/SearchFormFieldGroup'

import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import useToggle from 'hooks/useToggle'
import CarHireTimeSelect from 'components/CarHire/CarHireSearchBar/CarHireTimeSelect'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { EmptyArray } from 'lib/array/arrayUtils'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { addMonths, shortTimeHourFormatAmPm } from 'lib/datetime/dateUtils'
import Expander from 'components/utils/Expander'
import Caption from 'LuxKit/Typography/Caption'
import CarHireSearchDriversAgeCategorySelect from 'components/CarHire/CarHireSearchBar/CarHireSearchDriversAgeCategorySelect'
import styled from 'styled-components'
import { rem } from 'polished'
import CarHireDriversAgeInput from 'components/CarHire/CarHireSearchBar/CarHireDriversAgeInput'

interface Props {
  onSearch: () => void
  formId?: string
}

// Margin doesn't animate on slide down so we're left with
// double gap when this is closed. So fake it with a padding top instead
// this padding will get animated correctly
const DropOffLocationSlide = styled(Expander)`
  margin: 0;

  >:first-child {
    padding-top: ${rem(12)};
  }
`

const DriversAgeContainer = styled.div`
  opacity: 1;
  transition: opacity 0.2s;

  &.hidden {
    display: none;
    opacity: 0;
    pointer-events: none;
  }
`

const DriversAgeInput = styled(CarHireDriversAgeInput)`
  width: ${rem(118)};
`

enum SearchSteps {
  LOCATION = 'location',
  SECONDARY_LOCATION = 'secondaryLocation',
  DATE = 'date',
  PICKUP_TIME = 'pickUpTime',
  RETURN_TIME = 'returnTime',
}

const maxDate = addMonths(new Date(), 18)

function CarHireTabContent(props: Props) {
  const { onSearch, formId } = props
  const intl = useIntl()
  const carHireLabels = useCarHireSearchLabels()

  const activeDateToggleButton = {
    id: DATE_SEARCH_OPTION_IDS.SPECIFIC,
    label: intl.formatMessage({
      id: '6cHYyFM2ab3pyfPXyCjo',
      defaultMessage: 'Specific dates',
      description: 'Date toggle label for specific dates in car hire search',
    }),
  }

  const {
    checkinDate: pickUpDate,
    checkoutDate: returnDate,
    searchItem,
    secondarySearchItem,
    pickUpTime,
    dropOffTime,
    driversAgeCategory,
  } = useContext(GlobalSearchStateContext)
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const [showPickUpTimeSelect, toggleShowPickUpTimeSelect] = useToggle(false)
  const [showReturnTimeSelect, toggleShowReturnTimeSelect] = useToggle(false)
  const [differentLocations, setDifferentLocations] = useState<boolean>(!!(secondarySearchItem && searchItem?.format.mainText !== secondarySearchItem?.format.mainText))

  useEffect(() => {
    if (secondarySearchItem && searchItem?.format.mainText !== secondarySearchItem?.format.mainText) {
      setDifferentLocations(true)
    }
  }, [searchItem, secondarySearchItem])

  const setPickUpTimeDispatch = useCallback((time: string) => {
    searchDispatch({ type: GlobalSearchStateActions.SET_PICKUP_TIME, pickupTime: time })
    toggleShowPickUpTimeSelect()
  }, [searchDispatch, toggleShowPickUpTimeSelect])

  const setReturnTimeDispatch = useCallback((time: string) => {
    searchDispatch({ type: GlobalSearchStateActions.SET_RETURN_TIME, dropOffTime: time })
    toggleShowReturnTimeSelect()
  }, [searchDispatch, toggleShowReturnTimeSelect])

  const stepsValidation = useMemo(() => ({
    [SearchSteps.LOCATION]: searchItem,
    ...differentLocations && { [SearchSteps.SECONDARY_LOCATION]: differentLocations && secondarySearchItem },
    [SearchSteps.DATE]: pickUpDate && returnDate,
  }),
  [searchItem, differentLocations, secondarySearchItem, pickUpDate, returnDate])

  const [activeStep, setActiveStep] = useState<SearchSteps>()
  const formRef = useRef<HTMLFormElement>(null)

  const onSubmit = () => {
    if (formRef.current?.reportValidity()) {
      onSearch()
    }
  }

  const onDifferentLocationChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.currentTarget.checked
    if (!nextValue) {
      searchDispatch({ type: GlobalSearchStateActions.UNSET_SECONDARY_SEARCH_ITEM })
    }
    setDifferentLocations(nextValue)
  }, [searchDispatch])

  const {
    goToNextStep,
    goToPreviousStep,
    closeActiveStep,
    isSearchEnabled,
  } = useStepsController({ activeStep, setActiveStep, stepsValidation, onSearch: onSubmit })

  const dateSearchProps = useDateSearchProps({ onAnytimeDatesSelected: goToNextStep })

  const openPickUpSearchStep = useCallback(() => setActiveStep(SearchSteps.LOCATION), [setActiveStep])
  const openDropOffSearchStep = useCallback(() => setActiveStep(SearchSteps.SECONDARY_LOCATION), [setActiveStep])
  const openDateSearchStep = useCallback(() => setActiveStep(SearchSteps.DATE), [setActiveStep])

  const pickUpTimeFieldRef = useRef<HTMLButtonElement>(null)
  const returnTimeFieldRef = useRef<HTMLButtonElement>(null)

  const onSelectDriversAge = useCallback((nextCategory: App.CarHireDriverAgeCategory) => {
    searchDispatch({ type: GlobalSearchStateActions.SET_DRIVERS_AGE_CATEGORY, driversAgeCategory: nextCategory })
  }, [searchDispatch])

  const needsActualAge = driversAgeCategory === 1 || driversAgeCategory === 3

  const pickUpDateLabel = intl.formatMessage({
    id: 'QevHI935af3iegseeTip',
    defaultMessage: 'Pick-up date',
    description: 'Label for car hire pick-up date field in the date selection or calendar picker',
  })
  const startDatePlaceholder = intl.formatMessage({
    id: 'DEh-W2wjp2_vhxrVlxpW',
    defaultMessage: 'Start date',
    description: 'Placeholder for car hire pick-up date field in car hire section',
  })
  const timeLabel = intl.formatMessage({
    id: 'GNTnJP0h0IMl-XYzxyCy',
    defaultMessage: 'Time',
    description: 'Label for car hire time selection field in the car hire booking section',
  })
  const startTimePlaceholder = intl.formatMessage({
    id: 'S4Myn7A1DF0dtzEuCCD4',
    defaultMessage: 'Start time',
    description: 'Placeholder for car hire pick-up time field in car hire section',
  })
  const dropOffDateLabel = intl.formatMessage({
    id: 'YomcnCUa97-rCn5AKpM-',
    defaultMessage: 'Drop-off date',
    description: 'Label for car hire drop-off date field in the date selection or calendar picker',
  })
  const endDatePlaceholder = intl.formatMessage({
    id: 'bmcV-bjKZVe3FUTlsD4k',
    defaultMessage: 'End date',
    description: 'Placeholder for car hire drop-off date field in car hire section',
  })
  const endTimePlaceholder = intl.formatMessage({
    id: 'OCbg4CNoxXSLAigwIbfR',
    defaultMessage: 'End time',
    description: 'Placeholder for car hire drop-off time field in car hire section',
  })
  const driversAgeCaption = intl.formatMessage({
    id: 'kZT9LoBgX7HRTNWJGcOA',
    defaultMessage: 'How old is the driver?',
    description: 'Caption label for car hire driver age input in car hire section',
  })
  const searchLabel = intl.formatMessage({
    id: 'n6O83oBLK4xILIGlO_TF',
    defaultMessage: 'Search',
    description: 'Search button text for car hire search in the search and discovery interface',
  })
  const applyLabel = intl.formatMessage({
    id: 'rysJgQjkQAfN6vSrdcHs',
    defaultMessage: 'Apply',
    description: 'Apply button text for car hire date panel in car hire section',
  })
  const dateTitle = intl.formatMessage({
    id: 'qX2fXk8CtF6xo-YutuJT',
    defaultMessage: 'When do you want to hire?',
    description: 'Title for car hire date selection panel in the date selection or calendar picker',
  })

  const locationTitle = !differentLocations
    ? carHireLabels.pickUpDropOffLocationTitle
    : activeStep === SearchSteps.LOCATION
      ? carHireLabels.pickUpLocationTitle
      : carHireLabels.dropOffLocationTitle

  return (
    <div>
      <VerticalSpacer as="form" id={formId} ref={formRef} gap={16} onSubmit={onSubmit}>
        <VerticalSpacer gap={12}>
          <SearchFormField
            label={differentLocations ? carHireLabels.pickUpLocationTitle : carHireLabels.pickUpDropOffLocationTitle}
            placeholder={carHireLabels.pickUpLocationPlaceholder}
            value={searchItem?.format.mainText}
            onClick={openPickUpSearchStep}
            required
          />
          <DropOffLocationSlide expanded={differentLocations}>
            <SearchFormField
              label={carHireLabels.dropOffLocationTitle}
              placeholder={carHireLabels.pickUpLocationPlaceholder}
              value={secondarySearchItem?.format.mainText}
              onClick={openDropOffSearchStep}
              required={differentLocations}
            />
          </DropOffLocationSlide>
          <CheckboxInput
            defaultChecked={differentLocations}
            onChange={onDifferentLocationChange}
          >
            {intl.formatMessage({
              id: 'Eysf8IJtS1RIRGXqySs8',
              defaultMessage: 'Drop-off at a different location',
              description: 'Checkbox label for dropping off car at a different location',
            })}
          </CheckboxInput>
        </VerticalSpacer>

        <VerticalSpacer gap={12}>
          <SearchFormFieldGroup>
            <SearchFormField
              label={pickUpDateLabel}
              value={pickUpDate ? pickUpDate.format(DMY_CASUAL_FORMAT) : undefined}
              placeholder={startDatePlaceholder}
              onClick={openDateSearchStep}
              required
            />
            <div style={{ position: 'relative' }}>
              <SearchFormField
                ref={pickUpTimeFieldRef}
                label={timeLabel}
                placeholder={startTimePlaceholder}
                value={pickUpTime}
                displayValue={pickUpTime ? shortTimeHourFormatAmPm(new Date('1970-01-01T' + pickUpTime + 'Z'), true) : undefined}
                onClick={toggleShowPickUpTimeSelect}
                required
              />
              <CarHireTimeSelect
                open={showPickUpTimeSelect}
                toggleDropDown={toggleShowPickUpTimeSelect}
                dropdownTriggerRef={pickUpTimeFieldRef}
                onChange={setPickUpTimeDispatch}
                selectedTime={pickUpTime}
              />
            </div>
          </SearchFormFieldGroup>

          <SearchFormFieldGroup>
            <SearchFormField
              label={dropOffDateLabel}
              value={returnDate ? returnDate.format(DMY_CASUAL_FORMAT) : undefined}
              placeholder={endDatePlaceholder}
              onClick={openDateSearchStep}
              required
            />
            <div style={{ position: 'relative' }}>
              <SearchFormField
                ref={returnTimeFieldRef}
                label={timeLabel}
                placeholder={endTimePlaceholder}
                value={dropOffTime}
                displayValue={dropOffTime ? shortTimeHourFormatAmPm(new Date('1970-01-01T' + dropOffTime + 'Z'), true) : undefined}
                onClick={toggleShowReturnTimeSelect}
                required
              />
              <CarHireTimeSelect
                open={showReturnTimeSelect}
                toggleDropDown={toggleShowReturnTimeSelect}
                dropdownTriggerRef={returnTimeFieldRef}
                onChange={setReturnTimeDispatch}
                selectedTime={dropOffTime}
              />
            </div>
          </SearchFormFieldGroup>
        </VerticalSpacer>
        <CarHireSearchDriversAgeCategorySelect
          value={driversAgeCategory}
          onChange={onSelectDriversAge}
        />
        <DriversAgeContainer className={clsx({ hidden: !needsActualAge })}>
          <VerticalSpacer as="label" gap={8}>
            <Caption variant="large">
              {driversAgeCaption}
            </Caption>
            <DriversAgeInput />
          </VerticalSpacer>
        </DriversAgeContainer>
      </VerticalSpacer>

      {/* overlays */}
      <LocationFloatingPanel
        isOpen={activeStep === SearchSteps.LOCATION || activeStep === SearchSteps.SECONDARY_LOCATION}
        onClose={closeActiveStep}
        onGoBack={goToPreviousStep}
        onConfirm={goToNextStep}
        isSecondaryLocation={activeStep === SearchSteps.SECONDARY_LOCATION}
        searchTypes={CAR_HIRE_SEARCH_TYPES}
        typeaheadVertical="car_hire"
        placeholder={carHireLabels.pickUpLocationPlaceholder}
        title={locationTitle}
      />

      <DateFloatingPanel
        {...dateSearchProps}
        isOpen={activeStep === SearchSteps.DATE}
        maxDate={maxDate}
        onClose={closeActiveStep}
        onConfirm={isSearchEnabled ? onSearch : closeActiveStep}
        onGoBack={goToPreviousStep}
        confirmText={isSearchEnabled ? searchLabel : applyLabel}
        dateToggleButtons={EmptyArray}
        activeDateToggleButton={activeDateToggleButton}
        title={dateTitle}
        label="day"
        includeLastDayOnCount
        allowSameDayRange
      />
    </div>
  )
}

export default CarHireTabContent
